import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { Endpoints } from '@/api';
import Button from '@/design_system/Button';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import Stack from '@/design_system/Stack';
import { useShowToast } from '@/design_system/Toast';
import InlineText from '@/design_system/Typography/InlineText';
import IconAdd from '@/icons/Add.svg';
import { useEntities } from '@/models/entity';
import { type EntityGroupDetail, useUpdateEntityGroup } from '@/models/entityGroup';
import useDebouncedState from '@/utils/useDebouncedState';

const EntitySearchSelect = ({
  onSelect,
  entitiesToExcludeFromSearch,
  onBlur,
}: {
  onSelect: (entity: Endpoints['GET /entities']['response']['entities'][number] | null) => void;
  entitiesToExcludeFromSearch: EntityGroupDetail['entities'];
  onBlur: () => void;
}) => {
  const { t } = useLingui();

  const [query, debouncedQuery, setQuery] = useDebouncedState<string>('', 500);

  const { data: { entities } = { entities: [] }, isFetching } = useEntities({
    search: debouncedQuery || undefined,
  });

  const entityOptions = entities.filter(
    (entity) => !entitiesToExcludeFromSearch.find((e) => e.id === entity.id)
  );

  return (
    <InputSelect
      size="small"
      variant="add"
      aria-label={t({
        id: 'settings.entityGroups.edit.entities.search-select.label',
        message: 'Add an entity',
      })}
      placeholder={t({
        id: 'settings.entityGroups.edit.entities.search-select.placeholder',
        message: 'Search an entity to add...',
      })}
      isLoading={isFetching || query !== debouncedQuery}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      menuIsOpen
      value={null}
      inputValue={query}
      onInputChange={setQuery}
      filterOption={null}
      options={entityOptions}
      getOptionValue={(entity) => (entity ? entity.id : '')}
      getOptionLabel={(entity) => (entity ? entity.name : '')}
      onChange={onSelect}
      onBlur={onBlur}
      style={{ width: '15rem' }}
    />
  );
};

export const AddEntityToGroup = ({
  entityGroupId,
  entities,
}: {
  entityGroupId: string;
  entities: EntityGroupDetail['entities'];
}) => {
  const [showEntitySearchSelect, setShowEntitySearchSelect] = useState(false);
  const showToast = useShowToast();

  const { mutateAsync: updateEntityGroup } = useUpdateEntityGroup();

  const handleSelect = async (
    entity: Endpoints['GET /entities']['response']['entities'][number] | null
  ) => {
    setShowEntitySearchSelect(false);

    if (!entity) {
      return;
    }

    const entityName = entity.name;

    try {
      await updateEntityGroup({
        entityGroupId,
        body: {
          entitiesIds: [...entities.map((e) => e.id), entity.id],
        },
      });
      showToast({
        text: (
          <Trans id="settings.organization.entities.groups.detail.add.success">
            &quot;{entityName}&quot; was added to the group.
          </Trans>
        ),
        type: 'success',
      });
    } catch {
      showToast({
        text: (
          <Trans id="settings.organization.entities.groups.detail.add.error">
            Could not add &quot;{entityName}&quot; to the group. Please try again.
          </Trans>
        ),
        type: 'error',
      });
    }
  };

  return (
    <>
      {showEntitySearchSelect ? (
        <EntitySearchSelect
          onSelect={(entity) => {
            void handleSelect(entity);
          }}
          entitiesToExcludeFromSearch={entities}
          onBlur={() => {
            setShowEntitySearchSelect(false);
          }}
        />
      ) : (
        <Button
          variant="link-subtle"
          size="small"
          style={{ width: 'fit-content', padding: '8px' }}
          onPress={() => {
            setShowEntitySearchSelect(true);
          }}
        >
          <Stack row alignItems="center" gap="4px">
            <IconAdd />
            <InlineText size="200" weight="medium">
              <Trans id="settings.entityGroups.edit.entities.add">Add an entity to the group</Trans>
            </InlineText>
          </Stack>
        </Button>
      )}
    </>
  );
};
